import React, { useEffect, useState } from 'react'
import Menu from "../Menu";
import useApiUrls from "../../useApiUrls";

const SocketReqHistoryList = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [filters, setFilters] = useState({
        posid: '',
        type: '',
        fromdate: '',
        todate: ''
    });

    const [pageSize, setPageSize] = useState(20);
    const [pageNo, setPageNo] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const token = getCookie("token") || getCookie("authToken");
    const userid = getCookie("userid") || getCookie("userId");

    const [posOptions, setPosOptions] = useState([]);
    const [posLoading, setPosLoading] = useState(false);

    const { apiUrls } = useApiUrls();
    const typeOptions = apiUrls.find(u => u.operationType === 'SocketReqTypes')?.types || [];

    const fetchPosOptions = async () => {
        setPosLoading(true);
        try {
            const res = await fetch('https://api.dqr-display-tms.eventum.co.in/api/POSOperation/GetDistinct/posname', {
                method: 'GET',
                headers: {
                    accept: 'text/plain',
                    UserId: userid,
                    'X-Authorization': token,
                }
            });
            const data = await res.json();
            if (data && data.isresponse && Array.isArray(data.data)) {
                setPosOptions(data.data);
            }
        } catch (err) {
            console.error('Failed to fetch POS options:', err);
        } finally {
            setPosLoading(false);
        }
    };

    useEffect(() => {
        fetchPosOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const baseURL = 'https://api.dqr-display-tms.eventum.co.in/api/SocketReqOperation';

    const toEpochSeconds = (d, endOfDay = false) => {
        try {
            if (!d) return 0;
            const date = new Date(d);
            if (isNaN(date.getTime())) return 0;
            if (endOfDay) {
                date.setHours(23, 59, 59, 999);
            } else {
                date.setHours(0, 0, 0, 0);
            }
            return Math.floor(date.getTime() / 1000);
        } catch (_) {
            return 0;
        }
    };

    const buildPayload = () => {
        const applyDate = Boolean(filters.fromdate) || Boolean(filters.todate);
        return {
            posid: Number(filters.posid) || 0,
            applycreateddate: applyDate,
            fromcreateddate: toEpochSeconds(filters.fromdate, false),
            endcreateddate: toEpochSeconds(filters.todate, true),
            type: String(filters.type || ''),
            ispageload: filters.posid === '' && !applyDate, // Set to true if no filters
            pagesize: Number(pageSize),
            currentpageno: Number(pageNo),
            withoutpagination: false,
            pagedirection: 'first'
        };
    };

    const fetchHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${baseURL}/Search`, {
                method: 'POST',
                headers: {
                    accept: 'text/plain',
                    'Content-Type': 'application/json-patch+json',
                    UserId: userid,
                    'X-Authorization': token,
                },
                body: JSON.stringify(buildPayload()),
            });

            if (!res.ok) {
                const txt = await res.text();
                throw new Error(`HTTP ${res.status}: ${txt}`);
            }
            const data = await res.json();
            
            // Adjust based on common API response structures in this project
            let rows = [];
            if (data && data.data && Array.isArray(data.data)) {
                rows = data.data;
            } else if (Array.isArray(data)) {
                rows = data;
            } else if (data && typeof data === 'object') {
                // Sometimes it might be wrapped differently
                rows = data.items || data.list || [];
            }
            
            setItems(rows);
            setHasMore(rows.length === Number(pageSize));
        } catch (err) {
            setError('Failed to fetch socket request history: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNo, pageSize]);

    const onSearchSubmit = (e) => {
        e.preventDefault();
        setPageNo(0);
        fetchHistory();
    };

    const onResetFilters = () => {
        setFilters({ posid: '', type: '', fromdate: '', todate: '' });
        setPageNo(0);
        fetchHistory();
    };

    const goFirst = () => { if (pageNo !== 0) setPageNo(0); };
    const goPrev = () => { if (pageNo > 0) setPageNo(pageNo - 1); };
    const goNext = () => { if (hasMore) setPageNo(pageNo + 1); };

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogContent, setDialogContent] = useState('');

    const openRowDialog = (title, row) => {
        setDialogTitle(title);
        try {
            setDialogContent(JSON.stringify(row, null, 2));
        } catch (_) {
            setDialogContent(String(row));
        }
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setDialogContent('');
        setDialogTitle('');
    };

    return (
        <>
        <Menu />
        <div className="wrapper">
            <div className="content-wrapper">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--info)' }}>
                                    <h3 className="card-title mb-0">Socket Request History</h3>
                                    <div className="card-tools"></div>
                                </div>
                                <div className="card-body">
                                    {error && (
                                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                            {error}
                                            <button type="button" className="close" onClick={() => setError(null)}>
                                                <span>&times;</span>
                                            </button>
                                        </div>
                                    )}

                                    {/* Advanced Search */}
                                    <form className="mb-3" onSubmit={onSearchSubmit}>
                                        <div className="card card-body p-3 mb-2" style={{background:'#f8f9fa'}}>
                                            <div className="row">
                                                <div className="col-md-3 col-sm-6 mb-2">
                                                    <select 
                                                        className="form-control form-control-sm" 
                                                        value={filters.posid} 
                                                        onChange={(e)=>setFilters({...filters, posid: e.target.value})}
                                                        disabled={posLoading}
                                                    >
                                                        <option value="">All POS</option>
                                                        {posOptions.map(pos => (
                                                            <option key={pos.id} value={pos.id}>{pos.value}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-3 col-sm-6 mb-2">
                                                    <select 
                                                        className="form-control form-control-sm" 
                                                        value={filters.type} 
                                                        onChange={(e)=>setFilters({...filters, type: e.target.value})}
                                                    >
                                                        <option value="">All Types</option>
                                                        {typeOptions.map(type => (
                                                            <option key={type} value={type}>{type}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-2 col-sm-6 mb-2">
                                                    <input 
                                                        type="date" 
                                                        className="form-control form-control-sm" 
                                                        placeholder="From Date" 
                                                        value={filters.fromdate} 
                                                        onChange={(e)=>setFilters({...filters, fromdate: e.target.value})} 
                                                    />
                                                </div>
                                                <div className="col-md-2 col-sm-6 mb-2">
                                                    <input 
                                                        type="date" 
                                                        className="form-control form-control-sm" 
                                                        placeholder="To Date" 
                                                        value={filters.todate} 
                                                        onChange={(e)=>setFilters({...filters, todate: e.target.value})} 
                                                    />
                                                </div>
                                                <div className="col-md-2 col-sm-12 mb-2">
                                                    <div className="btn-group btn-block" role="group">
                                                        <button 
                                                            type="submit" 
                                                            className="btn btn-success btn-sm" 
                                                            disabled={loading}
                                                        >
                                                            <i className="fas fa-search mr-1"></i>
                                                        </button>
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-secondary btn-sm" 
                                                            onClick={onResetFilters} 
                                                            disabled={loading}
                                                        >
                                                            <i className="fas fa-redo mr-1"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {Object.entries(filters).filter(([,v]) => (v ?? '') !== '').length > 0 && (
                                            <div className="mb-2">
                                                <small className="text-muted">Active filters: </small>
                                                {Object.entries(filters).filter(([,v]) => (v ?? '') !== '').map(([k,v]) => {
                                                    let displayValue = v;
                                                    if (k === 'posid') {
                                                        const found = posOptions.find(p => String(p.id) === String(v));
                                                        if (found) displayValue = found.value;
                                                    }
                                                    return (
                                                        <span key={k} className="badge badge-light mr-2" style={{fontWeight:500}}>
                                                            {k === 'posid' ? 'POS' : k === 'fromdate' ? 'From' : k === 'todate' ? 'To' : k.charAt(0).toUpperCase() + k.slice(1)}: {displayValue}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </form>

                                    {loading && (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                            <p className="mt-2 text-muted">Loading socket request history...</p>
                                        </div>
                                    )}

                                    {!loading && items.length === 0 && !error && (
                                        <div className="alert alert-info">
                                            <i className="fas fa-info-circle mr-2"></i>No records found.
                                        </div>
                                    )}

                                    {!loading && items.length > 0 && (
                                        <>
                                        <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                                            <div className="mb-2">
                                                <span className="badge badge-primary p-2">Total Records: {items.length}</span>
                                            </div>
                                            <div className="mb-2">
                                                <div className="input-group input-group-sm" style={{width: 180}}>
                                                    <div className="input-group-prepend">
                                                        <span className="input-group-text">Page size</span>
                                                    </div>
                                                    <select 
                                                        className="form-control" 
                                                        value={pageSize} 
                                                        onChange={(e)=>setPageSize(Number(e.target.value))}
                                                    >
                                                        <option value={10}>10</option>
                                                        <option value={20}>20</option>
                                                        <option value={50}>50</option>
                                                        <option value={100}>100</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="table-responsive">
                                            <table className="table table-sm table-bordered table-striped table-hover">
                                                 <thead className="thead-light" style={{
                            backgroundColor: 'var(--surface2)',
                            color: 'var(--text)',
                            borderColor: 'var(--border)'
                          }}>
                                                    <tr className="text-nowrap">
                                                        <th>ID</th>
                                                        <th>Created Date</th>
                                                        <th>POS ID</th>
                                                        <th>POS Name</th>
                                                        <th>Merchant ID</th>
                                                        <th>Store ID</th>
                                                        <th>Type</th>
                                                        <th className="text-center">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="align-middle">
                                                    {items.map((row, idx) => (
                                                        <tr key={row.id || idx}>
                                                            <td>{row.id ?? '-'}</td>
                                                            <td className="text-nowrap">{formatDate(row.date || row.createddate)}</td>
                                                            <td>{row.posid ?? '-'}</td>
                                                            <td>{row.posname ?? '-'}</td>
                                                            <td>{row.merchantid ?? '-'}</td>
                                                            <td>{row.storeid ?? '-'}</td>
                                                            <td>{row.type ?? '-'}</td>
                                                            <td className="text-center text-nowrap">
                                                                <button 
                                                                    className="btn btn-sm btn-outline-primary" 
                                                                    onClick={() => openRowDialog('Request Details', row)}
                                                                    title="View full details"
                                                                >
                                                                    <i className="fas fa-eye mr-1"></i>View Details
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination */}
                                        <div className="d-flex justify-content-between align-items-center mt-3 p-3">
                                            <div>
                                                <button 
                                                    className="btn btn-outline-secondary btn-sm mr-2" 
                                                    onClick={goFirst} 
                                                    disabled={loading || pageNo === 0}
                                                >
                                                    <i className="fas fa-angle-double-left"></i> First
                                                </button>
                                                <button 
                                                    className="btn btn-outline-secondary btn-sm" 
                                                    onClick={goPrev} 
                                                    disabled={loading || pageNo === 0}
                                                >
                                                    <i className="fas fa-angle-left"></i> Prev
                                                </button>
                                            </div>
                                            <div>
                                                <span className="badge badge-light p-2">Page {pageNo + 1}</span>
                                            </div>
                                            <div>
                                                <button 
                                                    className="btn btn-outline-secondary btn-sm" 
                                                    onClick={goNext} 
                                                    disabled={loading || !hasMore}
                                                >
                                                    Next <i className="fas fa-angle-right"></i>
                                                </button>
                                            </div>
                                        </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Details Modal */}
        {dialogOpen && (
            <div className="modal" style={{display:'block', background:'rgba(0,0,0,0.5)', overflowY: 'auto'}}>
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <i className="fas fa-code mr-2"></i>{dialogTitle || 'Details'}
                            </h5>
                            <button type="button" className="close" onClick={closeDialog}>
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body" style={{maxHeight:'70vh', overflow:'auto', background:'#f8f9fa'}}>
                            <pre style={{
                                whiteSpace:'pre-wrap', 
                                wordBreak:'break-word', 
                                margin:0, 
                                padding: '15px', 
                                background: '#fff', 
                                border: '1px solid #dee2e6', 
                                borderRadius: '4px'
                            }}>
                                {dialogContent}
                            </pre>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={closeDialog}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}

export default SocketReqHistoryList;

function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
}

function formatDate(value) {
    if (!value) return '-';
    try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return String(value);
        return d.toLocaleString();
    } catch (_) {
        return String(value);
    }
}